import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

export const StrategicUploadOverview = () => {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['upload-metrics'],
    queryFn: async () => {
      const { data: uploads, error: uploadsError } = await supabase
        .from('file_uploads')
        .select('id, processing_status, processing_metadata, transactions_count, created_at, processed_at');

      if (uploadsError) throw uploadsError;

      const totalFiles = uploads?.length || 0;
      const completedFiles = uploads?.filter(u => u.processing_status === 'completed').length || 0;
      const failedFiles = uploads?.filter(u => u.processing_status === 'error').length || 0;
      
      // Calculate average processing time
      const processingTimes = uploads
        ?.filter(u => u.processed_at && u.created_at)
        .map(u => {
          const start = new Date(u.created_at).getTime();
          const end = new Date(u.processed_at!).getTime();
          return (end - start) / 60000; // minutes
        }) || [];
      
      const avgProcessingTime = processingTimes.length > 0
        ? processingTimes.reduce((a, b) => a + b, 0) / processingTimes.length
        : 0;

      // Count current bottlenecks (files stuck in processing > 5 minutes)
      const now = new Date().getTime();
      const bottlenecks = uploads?.filter(u => {
        if (u.processing_status !== 'processing') return false;
        const created = new Date(u.created_at).getTime();
        return (now - created) / 60000 > 5;
      }).length || 0;

      return {
        totalFiles,
        accuracy: completedFiles > 0 ? ((completedFiles / (completedFiles + failedFiles)) * 100).toFixed(1) : '0',
        avgProcessingTime: avgProcessingTime.toFixed(1),
        bottlenecks
      };
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-header-mb">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-4 bg-white rounded-2xl border border-brand-border">
            <Skeleton className="h-20" />
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-header-mb">
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="p-3 bg-brand-secondary rounded-full">
          <i className="fa-solid fa-gears text-brand-primary text-xl"></i>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Total Files Processed</p>
          <p className="text-2xl text-brand-text-primary font-space-grotesk font-bold">{metrics?.totalFiles || 0}</p>
        </div>
      </div>
      
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="p-3 bg-brand-secondary rounded-full">
          <i className="fa-solid fa-crosshairs text-brand-primary text-xl"></i>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Aggregate Accuracy</p>
          <p className="text-2xl text-brand-text-primary font-space-grotesk font-bold">{metrics?.accuracy || 0}%</p>
        </div>
      </div>
      
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="p-3 bg-brand-secondary rounded-full">
          <i className="fa-solid fa-gauge-high text-brand-primary text-xl"></i>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Avg. Processing Time</p>
          <p className="text-2xl text-brand-text-primary font-space-grotesk font-bold">{metrics?.avgProcessingTime || 0} min/file</p>
        </div>
      </div>
      
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="p-3 bg-brand-danger-bg rounded-full">
          <i className="fa-solid fa-traffic-light text-brand-danger text-xl"></i>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Processing Bottlenecks</p>
          <p className="text-2xl text-brand-danger font-space-grotesk font-bold">{metrics?.bottlenecks || 0}</p>
        </div>
      </div>
    </section>
  );
};